<template>
  <div class="performance-rates" v-loading="loading">
    <portal to="layout-nav-title">员工个人汇总表</portal>
    <el-form inline v-if="rawData.length > 1">
      <el-form-item label="业务员">
        <el-select v-model="selectedUser" placeholder="请选择业务员" filterable clearable>
          <el-option v-for="user in rawData" :key="user.user_id" :label="user.screen_name" :value="user.user_id" />
        </el-select>
      </el-form-item>
      <el-form-item>
        <DialogLoading
          :visible="exportLoading"
          description="正在生成导出文件，预计需要 1-3 分钟，请等待"
          :title="`导出处理中`"
        />
        <el-button type="primary" @click="handleExport">导出</el-button>
      </el-form-item>
    </el-form>
    <YearToggle title="员工个人汇总表" v-model="filters.year" @change="onYearChange" />
    <div class="box-table" v-if="!loading">
      <el-table
        :data="tableData"
        border
        height="100%"
        style="width: 100%"
        :row-class-name="getRowClassName"
        :span-method="handleSpanMethod"
      >
        <el-table-column
          prop="salesperson"
          label="业务员"
          min-width="120"
          :fixed="canFixedColumn ? 'left' : undefined"
          :cell-class-name="getSalespersonCellClassName"
        >
          <template #default="scope">
            <div v-if="scope.row.isFirst" class="salesperson-cell">
              {{ scope.row.salesperson }}
            </div>
          </template>
        </el-table-column>
        <el-table-column
          prop="month"
          label="月份"
          min-width="80"
          :fixed="canFixedColumn ? 'left' : undefined"
        >
          <template #default="scope">
            {{ scope.row.isEmpty ? '' : (scope.row.isTotal ? '合计' : Number(scope.row.month)) }}
          </template>
        </el-table-column>
        <el-table-column label="下单" header-align="center" class-name="order-header">
          <el-table-column prop="order_target" label="目标" min-width="100" class-name="order-column">
            <template #default="scope">
              <app-locale-string :value="get(scope.row, 'sales_target')" />
            </template>
          </el-table-column>
          <el-table-column prop="order_amount" label="金额" min-width="100" class-name="order-column">
            <template #default="scope">
              <app-locale-string :value="get(scope.row, 'sales_amount')" />
            </template>
          </el-table-column>
          <el-table-column prop="order_rate" label="完成率" min-width="100" class-name="order-column">
            <template #default="scope">
              {{ calculateRate(get(scope.row, 'sales_amount'), get(scope.row, 'sales_target')) }}
            </template>
          </el-table-column>
          <el-table-column prop="order_diff" label="差额" min-width="100" class-name="order-column">
            <template #default="scope">
              {{ calculateDiff(get(scope.row, 'sales_amount'), get(scope.row, 'sales_target')) }}
            </template>
          </el-table-column>
        </el-table-column>
        <el-table-column label="完结" header-align="center" class-name="finish-header">
          <el-table-column prop="finish_target" label="目标" min-width="100" class-name="finish-column">
            <template #default="scope">
              <app-locale-string :value="get(scope.row, 'finish_target')" />
            </template>
          </el-table-column>
          <el-table-column prop="finish_amount" label="金额" min-width="100" class-name="finish-column">
            <template #default="scope">
              <app-locale-string :value="get(scope.row, 'finish_amount')" />
            </template>
          </el-table-column>
          <el-table-column prop="finish_rate" label="完成率" min-width="100" class-name="finish-column">
            <template #default="scope">
              {{ calculateRate(get(scope.row, 'finish_amount'), get(scope.row, 'finish_target')) }}
            </template>
          </el-table-column>
          <el-table-column prop="finish_diff" label="差额" min-width="100" class-name="finish-column">
            <template #default="scope">
              {{ calculateDiff(get(scope.row, 'finish_amount'), get(scope.row, 'finish_target')) }}
            </template>
          </el-table-column>
        </el-table-column>
        <el-table-column label="发货" header-align="center" class-name="shipping-header">
          <el-table-column prop="shipping_target" label="目标" min-width="100" class-name="shipping-column">
            <template #default="scope">
              <app-locale-string :value="get(scope.row, 'shipping_target')" />
            </template>
          </el-table-column>
          <el-table-column prop="shipping_amount" label="金额" min-width="100" class-name="shipping-column">
            <template #default="scope">
              <app-locale-string :value="get(scope.row, 'shipping_amount')" />
            </template>
          </el-table-column>
          <el-table-column prop="shipping_diff" label="差额" min-width="100" class-name="shipping-column">
            <template #default="scope">
              {{ calculateDiff(get(scope.row, 'shipping_amount'), get(scope.row, 'shipping_target')) }}
            </template>
          </el-table-column>
        </el-table-column>
      </el-table>
    </div>
  </div>
</template>

<script>
import { Vue, Component, Mixins } from 'vue-property-decorator';
import Utils from '@/components/utils.vue';
import _ from 'lodash';
import moment from 'moment';
import { localeString } from '@/components/locale-string';
import YearToggle from '@/components/year-toggle.vue';
import { screenService } from 'beans-admin-plugin';
import DialogLoading from '@/components/dialog-loading.vue';
import { exportFile } from '@/utils/export';

@Component({
  components: {
    YearToggle,
    DialogLoading,
  },
})
export default class PerformanceRatesByYear extends Mixins(Vue, Utils) {
  exportLoading = false;
  get = _.get;

  rawData = [];
  selectedUser = null;
  loading = false;

  filters = {
    year: moment().year(),
  };

  get canFixedColumn() {
    return !screenService.isMobile;
  }

  get yearOptions() {
    const currentYear = moment().year();
    return Array.from({ length: 2 }, (_, i) => currentYear - i).reverse();
  }

  get maxMonth() {
    const currentDate = moment();
    if (this.filters.year < currentDate.year()) {
      return 12;
    }
    return currentDate.month() + 1;
  }

  calculateRate(amount, target) {
    const targetValue = parseFloat(target);
    if (!targetValue) return '-';

    const rate = (parseFloat(amount) / targetValue) * 100;
    return `${rate.toFixed(2)}%`;
  }

  calculateDiff(amount, target) {
    const targetValue = parseFloat(target);
    if (!targetValue) return '-';

    const diff = targetValue - parseFloat(amount);
    if (diff <= 0) {
      return '已达成';
    }
    return localeString({
      value: diff,
    });
  }

  getSalespersonCellClassName() {
    return 'no-hover-effect';
  }

  get tableData() {
    const tableData = [];
    const allMonths = Array.from(
      { length: this.maxMonth },
      (_, i) => String(i + 1).padStart(2, '0')
    );

    const data = this.selectedUser ? this.rawData.filter(item => item.user_id === this.selectedUser) : this.rawData;

    data.forEach((person, personIndex) => {
      // 初始化该业务员的合计数据
      const totalData = {
        sales_target: 0,
        sales_amount: 0,
        finish_target: 0,
        finish_amount: 0,
        shipping_target: 0,
        shipping_amount: 0,
      };

      // 为每个业务员创建月度数据
      allMonths.forEach((month, monthIndex) => {
        const monthKey = `${this.filters.year}-${month}`;
        const monthData = _.get(person, ['datum', monthKey], {});

        // 累加到合计中
        Object.keys(totalData).forEach(key => {
          const value = _.get(monthData, key, 0);
          totalData[key] += parseFloat(value) || 0;
        });

        tableData.push({
          salesperson: _.get(person, 'screen_name', ''),
          isFirst: monthIndex === 0,
          rowspan: this.maxMonth,
          month,
          sales_target: _.get(monthData, 'sales_target', 0),
          sales_amount: _.get(monthData, 'sales_amount', 0),
          finish_target: _.get(monthData, 'finish_target', 0),
          finish_amount: _.get(monthData, 'finish_amount', 0),
          shipping_target: _.get(monthData, 'shipping_target', 0),
          shipping_amount: _.get(monthData, 'shipping_amount', 0),
        });
      });

      // 添加合计行
      tableData.push({
        salesperson: _.get(person, 'screen_name', ''),
        isTotal: true,
        month: '合计',
        ...totalData
      });

      // 在每个业务员数据后添加空行（最后一个业务员除外）
      if (personIndex < data.length - 1) {
        tableData.push({
          isEmpty: true,
          salesperson: '',
          month: '',
          sales_target: null,
          sales_amount: null,
          finish_target: null,
          finish_amount: null,
          shipping_target: null,
          shipping_amount: null,
        });
      }
    });

    return tableData;
  }

  async fetchData() {
    try {
      this.loading = true;
      const res = await this.$request.get('/reports/sales_follows/performance_rates_by_year', {
        year: this.filters.year,
      });
      this.rawData = _.orderBy(res.details, item => {
        if (item.user_id === this.currentUser.id) {
          return 0;
        }
        return 1;
      });
    } catch (error) {
      console.error('获取数据失败:', error);
    } finally {
      this.loading = false;
    }
  }

  created() {
    this.fetchData();
  }

  onYearChange() {
    this.fetchData();
  }

  getRowClassName({ row }) {
    if (row.isEmpty) return 'empty-row';
    if (row.isTotal) return 'total-row';
    return '';
  }

  handleSpanMethod({ row, columnIndex }) {
    // 只处理业务员列（第一列）的合并
    if (columnIndex === 0) {
      if (row.isFirst && !row.isEmpty) {
        return {
          rowspan: this.maxMonth + 1, // +1 是为了包含合计行
          colspan: 1
        };
      } else if (!row.isEmpty) {
        return {
          rowspan: 0,
          colspan: 0
        };
      }
    }
    return {
      rowspan: 1,
      colspan: 1
    };
  }


  async handleExport() {
    try {
      this.exportLoading = true;
      await exportFile('reports/sales_follows/performance_rates_by_year', { year: this.filters.year, return_xlsx: true });
    } finally {
      this.exportLoading = false;
    }
  }
}
</script>

<style lang="scss" scoped>
// 定义颜色变量
$--color-primary: $primary;
$--color-success: #67c23a;
$--color-warning: #e6a23c;

.performance-rates {
  padding: 20px;
  height: 100%;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;

  .box-table {
    flex: 1;
    overflow: hidden;
  }

  :deep(.el-table) {
    // 空行样式
    .empty-row {
      height: 20px;
      background-color: #f5f7fa;

      td {
        border: none !important;
        padding: 0;

        &.el-table__cell {
          background-color: #f5f7fa;
        }

        .cell {
          display: none;
        }
      }

      &:hover {
        td {
          background-color: #f5f7fa !important;
        }
      }
    }

    // 合计行样式
    .total-row {
      font-weight: bold;

      td {
        color: $--color-primary !important;
      }

      &:hover {
        td {
          color: $--color-primary !important;
        }
      }
    }

    // 父级表头样式
    th.order-header {
      background-color: rgba($--color-primary, 0.15) !important;
    }
    th.finish-header {
      background-color: rgba($--color-success, 0.15) !important;
    }
    th.shipping-header {
      background-color: rgba($--color-warning, 0.15) !important;
    }

    // 子级表头样式
    th.order-column {
      background-color: rgba($--color-primary, 0.15) !important;
    }
    th.finish-column {
      background-color: rgba($--color-success, 0.15) !important;
    }
    th.shipping-column {
      background-color: rgba($--color-warning, 0.15) !important;
    }

    // 确保悬浮时样式保持
    th.order-header:hover,
    th.order-column:hover {
      background-color: rgba($--color-primary, 0.15) !important;
    }
    th.finish-header:hover,
    th.finish-column:hover {
      background-color: rgba($--color-success, 0.15) !important;
    }
    th.shipping-header:hover,
    th.shipping-column:hover {
      background-color: rgba($--color-warning, 0.15) !important;
    }
  }
}
</style>
